import { EditOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import {
	Button,
	Card,
	Col,
	message,
	Row,
	Space,
	Switch,
	Table,
	TableColumnType,
	Typography,
	Select,
	Spin,
	Empty,
	Pagination,
	Input,
} from "antd";
import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { httpRequest } from "../../helpers/api";
import { getErrorMessage } from "../../helpers/errorHandler";
import moment from "moment";
import useAuth from "../../hooks/useAuth";
import useFetchList from "../../hooks/useFetchList";
import { EExchangeBoardType, ExchangeBoardProperties, ListExchangeBoardQuery } from "../../types/exchangeBoard.type";
import CreateExchangeBoardModal from "./Components/CreateExchangeBoardModal";
import DetailExchangeBoardModal from "./Components/DetailExchangeBoardModal";
import TabExchangeBoard from "./Components/TabBoard";
import CitySidebar from "./Components/CitySidebar";
import FilterBudget from "./Components/FilterBudget";
import { confirmModal, successModal } from "../../helpers/modal";
import { doWhatsAppExchangeBoard } from "../../helpers/exchangeBoard";
import styled from "styled-components";
import PropertyCard from "./Components/PropertyCard";
import { usePropertyTypes } from "../../hooks/usePropertyType";

interface ExchangeBoardCardProperties {
	scrollY?: number;
}
const { Title } = Typography;

const formatNumberWithDots = (num: number | undefined | null): string => {
	if (num === undefined || num === null) return "";
	if (num === 0) return "0";
	return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

const priceShorten = (price: number, lng: string = "id") => {
	const thousand = lng === "en" ? "K" : "Rb";
	const million = lng === "en" ? "M" : "Jt";
	const billion = lng === "en" ? "B" : "M";

	if (price >= 1000000000) {
		return `${(price / 1000000000).toFixed(price % 1000000000 === 0 ? 0 : 1)} ${billion}`;
	}
	if (price >= 1000000) {
		return `${(price / 1000000).toFixed(price % 1000000 === 0 ? 0 : 1)} ${million}`;
	}
	if (price >= 1000) {
		return `${(price / 1000).toFixed(price % 1000 === 0 ? 0 : 1)} ${thousand}`;
	}
	return price.toString();
};

const fetchCityGroupName = async (cityGroupId?: string) => {
	if (!cityGroupId) return "";
	try {
		const { data } = await httpRequest.get(`group-of-city/${cityGroupId}`);
		return (data as any).payload.cityGroupName || "";
	} catch {
		return "";
	}
};

const ExchangeBoardCard: React.FC<ExchangeBoardCardProperties> = ({ scrollY }) => {
	const routerLocation = useLocation();
	const { user, isAdminHQPlus, isOfficeManagementNonFinance, isAgent } = useAuth();
	const [showAdd, setShowAdd] = useState(false);
	const [isToggleLoading, setIsToggleLoading] = useState(false);
	const [editItem, setEditItem] = useState<ExchangeBoardProperties>();
	const [selectedCity, setSelectedCity] = useState<string>("");
	const [selectedCityName, setSelectedCityName] = useState<string>("");
	const [selectedType, setSelectedType] = useState<string>("");
	const [detailItem, setDetailItem] = useState<ExchangeBoardProperties>();
	// const [listingPropertyTypeList, setListingPropertyTypeList] = useState<ListingPropertyTypeProperties[]>([]);
	const [budgetRange, setBudgetRange] = useState<[number | undefined, number | undefined]>([undefined, undefined]);
	const [selectedPropertyType, setSelectedPropertyType] = useState<string | undefined>(undefined);
	const [contentHeight, setContentHeight] = useState<number>(0);
	const contentRef = useRef<HTMLDivElement>(null);
	const [isFetchingById, setIsFetchingById] = useState(false);
	const [cityName, setCityName] = useState<Record<string, string>>({});

	const includeData = "picUser,picProfile,listingPropertyType,picEmployee,picEmployee.office,picOffice";
	const { isLoading, data, query, setQuery, fetchList, pagination, changePage, search, setSearch } = useFetchList<
		ExchangeBoardProperties,
		ListExchangeBoardQuery
	>({
		endpoint: "exchange-boards",
		initialQuery: {
			sort: "createdAt:DESC",
			filterIsPublished: "true",
			includeData,
			limit: 10,
		},
	});

	const CustomSelect = styled(Select)`
		width: 100%;
		.ant-select {
			font-size: 13px !important;
		}
		.ant-select-selector {
			background-color: #f2f6fd !important;
			border: none !important;
			border-radius: 100px !important;
			.ant-select-selection-placeholder {
				font-size: 13px !important;
				color: black !important;
			}
		}

		.ant-select-selection-item {
			font-size: 13px !important;
		}
	`;

	const { propertyTypes: listingPropertyTypeList } = usePropertyTypes();
	// useEffect(() => {
	// 	const abort = new AbortController();

	// 	(async () => {
	// 		try {
	// 			let { data } = await httpRequest.get<BaseResponsePaginationProps<ListingPropertyTypeProperties>>(
	// 				"listing-property-types?filterIsPublished=true"
	// 			);
	// 			const sortedType = data.payload.results.sort((a, b) =>
	// 				a.name.localeCompare(b.name, 'id')
	// 			);
	// 			setListingPropertyTypeList(sortedType);
	// 		} catch (err) {
	// 			console.error(err);

	// 			message.error(getErrorMessage(err));
	// 		}
	// 	})();

	// 	return () => {
	// 		abort.abort();
	// 	};
	// }, []);

	useEffect(() => {
		const fetchExchangeBoardById = async (id: string) => {
			setIsFetchingById(true);
			try {
				const { data } = await httpRequest.get<{ payload: ExchangeBoardProperties }>(
					`exchange-boards/${id}?includeData=${includeData}`
				);
				console.log("Fetched request board by ID:", data.payload);

				const exchangeBoard = data.payload;

				if (exchangeBoard.picUserId && !exchangeBoard.picUser) {
					try {
						const userResponse = await httpRequest.get<any>(
							`users/${exchangeBoard.picUserId}?includeData=profile`
						);
						exchangeBoard.picUser = userResponse.data.payload;
						exchangeBoard.picProfile = userResponse.data.payload.profile;
					} catch (err) {
						console.error("Error fetching picUser:", err);
					}
				}

				if (exchangeBoard.picEmployeeId && !exchangeBoard.picEmployee) {
					try {
						const employeeResponse = await httpRequest.get<any>(
							`employees/${exchangeBoard.picEmployeeId}?includeData=office`
						);
						exchangeBoard.picEmployee = employeeResponse.data.payload;
					} catch (err) {
						console.error("Error fetching picEmployee:", err);
					}
				}

				if (exchangeBoard.picOfficeId && !exchangeBoard.picOffice) {
					try {
						const officeResponse = await httpRequest.get<any>(`offices/${exchangeBoard.picOfficeId}`);
						exchangeBoard.picOffice = officeResponse.data.payload;
					} catch (err) {
						console.error("Error fetching picOffice:", err);
					}
				}

				if (exchangeBoard.listingPropertyTypeId && !exchangeBoard.listingPropertyType) {
					// try {
					// 	const propertyTypeResponse = await httpRequest.get<any>(
					// 		`listing-property-types/${exchangeBoard.listingPropertyTypeId}`
					// 	);
					// 	exchangeBoard.listingPropertyType = propertyTypeResponse.data.payload;
					// } catch (err) {
					// 	console.error("Error fetching listingPropertyType:", err);
					// }

					const findPropertyType = listingPropertyTypeList.find(
						(v) => v.listingPropertyTypeId === exchangeBoard.listingPropertyTypeId
					);
					exchangeBoard.listingPropertyType = findPropertyType;
				}

				setDetailItem(exchangeBoard);
			} catch (err) {
				console.error(err);
				message.error(getErrorMessage(err));
			} finally {
				setIsFetchingById(false);
			}
		};

		const urlParams = new URLSearchParams(routerLocation.search);
		const exchangeBoardId = urlParams.get("id");

		if (exchangeBoardId) {
			fetchExchangeBoardById(exchangeBoardId);
		}
	}, [routerLocation.key]);

	useEffect(() => {
		if (!query.filterCreatedByUserId) {
			const newQuery: any = {
				sort: "createdAt:DESC",
				filterIsPublished: "true",
				includeData,
				offset: 0,
				limit: 10,
			};

			if (selectedCity) {
				newQuery.filterCityGroupId = selectedCity;
			}

			if (selectedType) {
				newQuery.filterType = selectedType;
			}

			if (budgetRange[0] !== undefined) {
				newQuery.filterBudgetMin = budgetRange[0];
			}

			if (budgetRange[1] !== undefined) {
				newQuery.filterBudgetMax = budgetRange[1];
			}

			if (selectedPropertyType) {
				newQuery.filterListingPropertyTypeId = selectedPropertyType;
			}

			setQuery(newQuery);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedCity, selectedType, budgetRange, selectedPropertyType]);

	useEffect(() => {
		const updateHeight = () => {
			if (contentRef.current) {
				setContentHeight(contentRef.current.offsetHeight);
			}
		};

		updateHeight();
		window.addEventListener("resize", updateHeight);

		const observer = new ResizeObserver(updateHeight);
		if (contentRef.current) {
			observer.observe(contentRef.current);
		}

		return () => {
			window.removeEventListener("resize", updateHeight);
			observer.disconnect();
		};
	}, [data, isLoading]);

	useEffect(() => {
		const fetchCityName = async () => {
			if (data.length === 0) return;

			const validCityGroupIds = data
				.map((item) => item.cityGroupId)
				.filter((id): id is string => typeof id === "string" && id.length > 0 && !cityName[id]);

			const uniqueCityGroupIds = Array.from(new Set(validCityGroupIds));

			if (uniqueCityGroupIds.length === 0) return;

			const newCityName: Record<string, string> = { ...cityName };

			await Promise.all(
				uniqueCityGroupIds.map(async (cityGroupId) => {
					const cityName = await fetchCityGroupName(cityGroupId);
					if (cityName) {
						newCityName[cityGroupId] = cityName;
					}
				})
			);

			setCityName(newCityName);
		};

		fetchCityName();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	const _handleChangeTab = (key: string) => {
		if (key === "my_board") {
			setQuery({
				filterCreatedByUserId: user.userId,
				filterIsPublished: "",
				includeData,
				sort: "createdAt:DESC",
				limit: 10,
				offset: 0,
			});
		} else {
			const newQuery: any = {
				filterCreatedByUserId: "",
				filterIsPublished: "true",
				includeData,
				sort: "createdAt:DESC",
				limit: 10,
				offset: 0,
			};

			if (selectedCity) {
				newQuery.filterCityGroupId = selectedCity;
			}

			if (selectedType) {
				newQuery.filterType = selectedType;
			}

			if (budgetRange[0] !== undefined) {
				newQuery.filterBudgetMin = budgetRange[0];
			}

			if (budgetRange[1] !== undefined) {
				newQuery.filterBudgetMax = budgetRange[1];
			}

			if (selectedPropertyType) {
				newQuery.filterListingPropertyTypeId = selectedPropertyType;
			}

			setQuery(newQuery);
		}
	};

	const _togglePublish = async (exchangeBoardId: string, isPublished: boolean) => {
		setIsToggleLoading(true);
		try {
			let dto = {
				isPublished,
			};
			await httpRequest.patch("exchange-boards/" + exchangeBoardId, dto);
			fetchList();
		} catch (err) {
			message.error(getErrorMessage(err));
		}
		setIsToggleLoading(false);
	};

	const doDelete = async (exchangeBoardId: string) => {
		if (!exchangeBoardId) {
			return message.error("Error in client!");
		}
		try {
			await httpRequest.delete("exchange-boards/" + exchangeBoardId);
			fetchList();
			successModal("Deleted!");
		} catch (error) {
			message.error(getErrorMessage(error));
		}
	};

	const renderExchangeBoardCard = (item: ExchangeBoardProperties) => {
		const typeConfig: Record<EExchangeBoardType, { label: string; bgColor: string; textColor: string }> = {
			[EExchangeBoardType.WTB]: { label: "WTB", bgColor: "#52C41A", textColor: "white" },
			[EExchangeBoardType.WTR]: { label: "WTR", bgColor: "#366CF4", textColor: "white" },
			[EExchangeBoardType.FOR_SALE]: { label: "For Sale", bgColor: "#e6f7ff", textColor: "#1890ff" },
			[EExchangeBoardType.FOR_RENT]: { label: "For Rent", bgColor: "#fff1f0", textColor: "#ff4d4f" },
		};
		const config = typeConfig[item.type];
		const host = window.location.protocol + "//" + window.location.host;
		const shareUrl = `${host}/request-board?id=${item.exchangeBoardId}`;

		const listingType =
			item.type === EExchangeBoardType.WTB
				? "Buyer"
				: item.type === EExchangeBoardType.WTR
					? "Renter"
					: "Listing";
		const location = cityName[item.cityGroupId || ""];

		const contentText =
			`Hot ${listingType} - ${location}!\n\n` +
			`${item.titlePlainText || "-"}\n` +
			`LT/LB: ${item.landSize || "-"}/${item.buildingSize || "-"} m²\n` +
			`${item.budget ? `Price: Rp ${priceShorten(item.budget)}` : ""}\n\n` +
			`Contact your trusted agent:\n` +
			`${item.picUser?.nickName || item.picUser?.name} - ${item.picProfile?.firstPhoneNumber ?? "Not Set"}\n\n` +
			`Cek detail lengkap di sini:\n`;

		const propertyInfo = (
			<Row
				gutter={12}
				style={{ minHeight: "32px", padding: "10px 0" }}
			>
				{item.landSize !== undefined && item.landSize !== null && (
					<Col>
						<Space
							size={4}
							style={{ fontSize: "12px", color: "#666" }}
						>
							<div
								style={{
									width: 20,
									height: 20,
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									backgroundColor: "#E3E9F4",
									borderRadius: "4px",
								}}
							>
								<img
									src="/images/landSize.svg"
									alt="land size"
									style={{ width: 12, height: 12 }}
								/>
							</div>
							<span>{formatNumberWithDots(item.landSize)} (m²)</span>
						</Space>
					</Col>
				)}
				{item.buildingSize !== undefined && item.buildingSize !== null && (
					<Col>
						<Space
							size={4}
							style={{ fontSize: "12px", color: "#666" }}
						>
							<div
								style={{
									width: 20,
									height: 20,
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									backgroundColor: "#E3E9F4",
									borderRadius: "4px",
								}}
							>
								<img
									src="/images/buildingSize.svg"
									alt="building size"
									style={{ width: 12, height: 12 }}
								/>
							</div>
							<span>{formatNumberWithDots(item.buildingSize)} (m²)</span>
						</Space>
					</Col>
				)}
				{item.budget && (
					<Col>
						<Space
							size={4}
							style={{ fontSize: "12px", color: "#666" }}
						>
							<div
								style={{
									width: 20,
									height: 20,
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									backgroundColor: "#E3E9F4",
									borderRadius: "4px",
								}}
							>
								<img
									src="/images/wallet.svg"
									alt="budget"
									style={{ width: 12, height: 12 }}
								/>
							</div>
							<span>{priceShorten(item.budget)}</span>
						</Space>
					</Col>
				)}
			</Row>
		);

		return (
			<PropertyCard
				avatarSrc={item.picUser?.profilePic}
				userName={item.picUser?.nickName?.toUpperCase() || item.picUser?.name}
				officeName={item.picEmployee?.office?.officeName}
				postingType={config}
				propertyType={item.listingPropertyType?.name}
				createdAt={item.createdAt}
				title={item.title || ""}
				propertyInfo={propertyInfo}
				onViewDetail={() => setDetailItem(item)}
				onContact={async () => {
					const areaName = await fetchCityGroupName(item.cityGroupId);
					doWhatsAppExchangeBoard(item, areaName);
				}}
				isContactDisabled={item.isExpired}
				shareUrl={shareUrl}
				shareText={contentText}
				showDelete={isAdminHQPlus}
				onDelete={() => {
					confirmModal({
						title: "Delete Request Board Post",
						subtitle: 'Are you sure to delete post from "' + item.picUser?.name + '"?',
						danger: true,
						onOk: async () => {
							await doDelete(item.exchangeBoardId);
						},
					});
				}}
			/>
		);
	};

	const COLUMNS: TableColumnType<ExchangeBoardProperties>[] = [
		{
			title: "CONTENT",
			key: "content",
			dataIndex: "title",
			width: 200,
			render: (title: string, record: ExchangeBoardProperties) => {
				return (
					<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
						<div
							style={{
								fontSize: "14px",
								color: "#333",
								lineHeight: "1.5",
								display: "-webkit-box",
								WebkitLineClamp: 3,
								WebkitBoxOrient: "vertical",
								overflow: "hidden",
								textOverflow: "ellipsis",
								minHeight: "60px",
								margin: "0",
							}}
							dangerouslySetInnerHTML={{
								__html: (title || "").replace(/<p>/g, '<p style="margin:0;padding:0">'),
							}}
						/>
					</div>
				);
			},
		},
		{
			title: "TYPE",
			key: "type",
			dataIndex: "type",
			width: 100,
			render: (type: EExchangeBoardType) => {
				const typeConfig: Record<EExchangeBoardType, { label: string; color: string; bgColor: string }> = {
					[EExchangeBoardType.WTB]: { label: "WTB", color: "white", bgColor: "#52C41A" },
					[EExchangeBoardType.WTR]: { label: "WTR", color: "white", bgColor: "#366CF4" },
					[EExchangeBoardType.FOR_SALE]: { label: "For Sale", color: "#1890ff", bgColor: "#e6f7ff" },
					[EExchangeBoardType.FOR_RENT]: { label: "For Rent", color: "#722ed1", bgColor: "#f9f0ff" },
				};
				const config = typeConfig[type];

				return (
					<span
						style={{
							padding: "4px 12px",
							borderRadius: "100px",
							backgroundColor: config.bgColor,
							color: config.color,
							fontSize: "12px",
							fontWeight: 600,
							display: "inline-block",
						}}
					>
						{config.label}
					</span>
				);
			},
		},
		{
			title: "CREATED AT",
			key: "createdAt",
			dataIndex: "createdAt",
			width: 180,
			sorter: (a: ExchangeBoardProperties, b: ExchangeBoardProperties) => {
				return moment(a.createdAt).unix() - moment(b.createdAt).unix();
			},
			defaultSortOrder: "descend" as const,
			showSorterTooltip: false,
			render: (createdAt: string, record: ExchangeBoardProperties) => {
				// const isUpdated = record.updatedAt && !moment(record.updatedAt).isSame(moment(createdAt), 'second');
				return (
					<Space
						direction="vertical"
						size={8}
						style={{ fontSize: "13px", color: "#666" }}
					>
						<span>{moment(createdAt).format("DD MMM YYYY, HH:mm")}</span>
						{record.updatedAt && !moment(record.updatedAt).isSame(moment(createdAt), "second") && (
							<span style={{ fontSize: "12px", color: "darkgray" }}>
								Last Update: {moment(record.updatedAt).format("DD MMM YYYY, HH:mm")}
							</span>
						)}
					</Space>
				);
			},
		},
		{
			title: "INFO",
			key: "info",
			width: 200,
			render: (_: any, record: ExchangeBoardProperties) => {
				return (
					<Space
						direction="horizontal"
						size={11}
						style={{ fontSize: "13px", color: "#666" }}
					>
						{record.landSize !== undefined && record.landSize !== null && (
							<Space size={4}>
								<div
									style={{
										width: 20,
										height: 20,
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										backgroundColor: "#E3E9F4",
										borderRadius: "4px",
									}}
								>
									<img
										src="/images/landSize.svg"
										alt="land size"
										style={{ width: 12, height: 12 }}
									/>
								</div>
								<span>{formatNumberWithDots(record.landSize)} (m²)</span>
							</Space>
						)}
						{record.buildingSize !== undefined && record.buildingSize !== null && (
							<Space size={4}>
								<div
									style={{
										width: 20,
										height: 20,
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										backgroundColor: "#E3E9F4",
										borderRadius: "4px",
									}}
								>
									<img
										src="/images/buildingSize.svg"
										alt="building size"
										style={{ width: 12, height: 12 }}
									/>
								</div>
								<span>{formatNumberWithDots(record.buildingSize)} (m²)</span>
							</Space>
						)}
						{record.budget && (
							<Space size={4}>
								<div
									style={{
										width: 20,
										height: 20,
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										backgroundColor: "#E3E9F4",
										borderRadius: "4px",
									}}
								>
									<img
										src="/images/wallet.svg"
										alt="budget"
										style={{ width: 12, height: 12 }}
									/>
								</div>
								<span>{priceShorten(record.budget)}</span>
							</Space>
						)}
					</Space>
				);
			},
		},
		query.filterCreatedByUserId
			? {
					title: "STATUS",
					key: "isPublished",
					dataIndex: "isPublished",
					width: 100,
					render: (isPublished: boolean, record: ExchangeBoardProperties) => (
						<Switch
							onChange={(checked) => _togglePublish(record.exchangeBoardId, checked)}
							loading={isToggleLoading}
							checked={isPublished}
							style={{
								backgroundColor: isPublished ? "#52c41a" : undefined,
							}}
						/>
					),
				}
			: null,
		query.filterCreatedByUserId
			? {
					title: "",
					key: "exchangeBoardId",
					dataIndex: "exchangeBoardId",
					width: 80,
					render: (exchangeBoardId: string, exchangeBoard: ExchangeBoardProperties) => (
						<Button
							disabled={exchangeBoard.createdByUserId !== user.userId}
							type="primary"
							icon={<EditOutlined />}
							style={{
								backgroundColor: "#0f1f3d",
								borderColor: "#0f1f3d",
								borderRadius: "8px",
								height: "40px",
								width: "40px",
							}}
							onClick={() => setEditItem(exchangeBoard)}
						/>
					),
				}
			: null,
	].filter(Boolean) as TableColumnType<ExchangeBoardProperties>[];

	return (
		<Card
			className="full-height"
			size="small"
			bordered={false}
		>
			<div style={{ marginBottom: 20 }}>
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Space
						direction="vertical"
						size={0}
					>
						<Typography.Text style={{ color: "#999", fontSize: "13px" }}>Request Board</Typography.Text>
						<Typography.Text
							style={{
								fontSize: 18,
								fontWeight: "500",
							}}
						>
							WTB & WTR Request Only
						</Typography.Text>
					</Space>

					{isOfficeManagementNonFinance || isAgent ? (
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={() => setShowAdd(true)}
							style={{ margin: 0, fontSize: "12px" }}
						>
							Create Posting
						</Button>
					) : (
						<></>
					)}
				</div>
			</div>

			<Space
				direction="vertical"
				style={{ width: "100%" }}
			>
				{isOfficeManagementNonFinance || isAgent ? (
					<TabExchangeBoard
						activeKey={query.filterCreatedByUserId ? "my_board" : "board"}
						onChange={_handleChangeTab}
						items={[
							{
								label: <span style={{ fontSize: 13, fontWeight: 500 }}>Request Board</span>,
								key: "board",
							},
							{
								label: <span style={{ fontSize: 13, fontWeight: 500 }}>My Postings</span>,
								key: "my_board",
							},
						]}
						style={{
							backgroundColor: "transparent",
						}}
						tabBarStyle={{
							backgroundColor: "#f5f5f5",
							borderRadius: "100px",
							marginBottom: "20px",
						}}
					/>
				) : (
					<></>
				)}

				{!query.filterCreatedByUserId && (
					<Row
						gutter={24}
						style={{ display: "flex", alignItems: "flex-start" }}
					>
						<Col
							xs={24}
							lg={8}
							xl={6}
							style={{ display: "flex" }}
						>
							<CitySidebar
								selectedCity={selectedCity}
								selectedType={selectedType}
								onCityChange={(cityGroupId, cityGroupName) => {
									setSelectedCity(cityGroupId);
									setSelectedCityName(cityGroupName);
								}}
								onTypeChange={setSelectedType}
								userCity={user?.city}
								maxHeight={contentHeight}
							/>
						</Col>

						<Col
							xs={24}
							lg={16}
							xl={18}
						>
							<div ref={contentRef}>
								<Space
									direction="vertical"
									style={{ width: "100%" }}
									size={16}
								>
									<Row
										justify="space-between"
										align="middle"
									>
										<Title
											level={5}
											style={{ margin: 0 }}
										>
											{selectedType === "WTB"
												? "Hot Buyer"
												: selectedType === "WTR"
													? "Hot Renter"
													: "Hot Buyer/Renter"}
											{selectedCityName && ` Area ${selectedCityName}`}
										</Title>
									</Row>

									<Row gutter={12}>
										<Col>
											<CustomSelect
												placeholder="All Property Type"
												style={{
													width: 180,
													borderRadius: "100px",
													backgroundColor: "#F5F7FF",
												}}
												allowClear
												value={selectedPropertyType}
												onChange={(value) =>
													setSelectedPropertyType(value as string | undefined)
												}
												options={listingPropertyTypeList.map((item) => ({
													value: item.listingPropertyTypeId,
													label: item.name,
												}))}
											/>
										</Col>
										<Col style={{ alignContent: "center" }}>
											<FilterBudget
												value={budgetRange}
												onChange={setBudgetRange}
											/>
										</Col>
									</Row>

									<div
										style={{
											height: scrollY || 600,
											overflowY: "auto",
											overflowX: "hidden",
											padding: "8px",
										}}
									>
										{isLoading ? (
											<div style={{ textAlign: "center", padding: "40px 0" }}>
												<Spin />
											</div>
										) : data.length === 0 ? (
											<Empty description="No request boards found" />
										) : (
											<Row gutter={[16, 16]}>
												{data.map((item) => (
													<Col
														xs={24}
														lg={12}
														key={item.exchangeBoardId}
													>
														{renderExchangeBoardCard(item)}
													</Col>
												))}
											</Row>
										)}
									</div>

									{!isLoading && data.length > 0 && (
										<Row
											justify="end"
											style={{ padding: "8px" }}
										>
											<Pagination
												style={{ margin: 0 }}
												current={pagination.page}
												pageSize={pagination.perPage}
												total={pagination.totalData}
												onChange={(page, pageSize) => changePage(page, pageSize)}
												showSizeChanger={false}
												showTotal={(total, range) =>
													`${range[0]}-${range[1]} of ${total} items`
												}
											/>
										</Row>
									)}
								</Space>
							</div>
						</Col>
					</Row>
				)}

				{query.filterCreatedByUserId && (
					<div
						style={{
							height: scrollY || 600,
							overflow: "hidden",
							display: "flex",
							flexDirection: "column",
						}}
					>
						<Input
							placeholder="Search Content..."
							suffix={<SearchOutlined />}
							allowClear
							onChange={(e) => setSearch(e.target.value)}
							value={search}
							style={{
								maxWidth: 400,
								borderRadius: "8px",
								marginBottom: 16,
							}}
						/>
						{isLoading ? (
							<div style={{ textAlign: "center", padding: "100px 0", flex: 1 }}>
								<Spin />
							</div>
						) : (
							<div style={{ flex: 1, overflow: "hidden" }}>
								<Table
									className="no-lr-padding table-exchange-board"
									loading={false}
									columns={COLUMNS}
									dataSource={data}
									rowKey="exchangeBoardId"
									pagination={false}
									scroll={{
										y: scrollY ? scrollY - 100 : 500,
										x: "100%",
									}}
									style={{
										borderRadius: "8px",
									}}
								/>
							</div>
						)}
					</div>
				)}
			</Space>

			<CreateExchangeBoardModal
				open={showAdd}
				onClose={() => setShowAdd(false)}
				onSuccess={() => _handleChangeTab("my_board")}
			/>

			<CreateExchangeBoardModal
				isEdit={true}
				open={editItem !== undefined}
				onClose={() => setEditItem(undefined)}
				onSuccess={() => fetchList()}
				exchangeBoard={editItem}
			/>

			<DetailExchangeBoardModal
				open={detailItem !== undefined}
				onClose={() => {
					setDetailItem(undefined);
					if (routerLocation.search) {
						window.history.replaceState({}, "", window.location.pathname);
					}
				}}
				exchangeBoard={detailItem}
			/>

			{isFetchingById && (
				<div
					style={{
						position: "fixed",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: "rgba(0, 0, 0, 0.45)",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						zIndex: 9999,
					}}
				>
					<Spin size="large" />
				</div>
			)}
		</Card>
	);
};

export default ExchangeBoardCard;
